import React, {
  useEffect,
  useRef,
  Fragment,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useIntl } from "react-intl";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { configEventCategory } from "../../../../.globals/helpers";
import { useSupervisorPermission } from "../../../../../.common/utils/supervisorPermission";
import { HasPermission } from "../../../../../../core/security";
import { LogManagerConsole } from "../../../../../../core/logManager";
import {
  speedFormatMPH,
  GetFormatForMoment,
} from "../../../../../../core/data/Helpers";

//Material UI
import { Container, Paper, Grid, Divider, Skeleton, Tabs, Tab, Box } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";

//Components
import {
  Form,
  LinearProgress,
  Loading,
  Checkbox,
  Button,
  Pill,
  PillsGroup,
  IconButton,
  TextArea,
  Icons,
  Label
} from "@gpstrackit/gtc-react-controls";
import { Breadcrumb } from "Components";
import { Timezone } from "Components/uiControls";
import { NotificationManager } from "Components/Notifications";

import Map from "../../../../../../components/Map/components/SimpleMap";
import { TagsGroup } from "../../../../../../components/Tag";
import VidFleetVideo from "../../../../../.common/components/VidFleetVideo";
import SmartWitnessSnapshot from "../../../../../.common/components/SmartWitnessSnapshot";
import Carousel from "react-material-ui-carousel";
import DeleteVideoModal from "../../../../modals/delete";
import NextPreviousButtons from "./components/nextPrevious";
import WizardAlert from "Modules/alerts/components/wizardAlert";

import {
  reset,
  getMediaDetail,
  saveNote,
  saveVideoReview
} from "./redux/slice";
import { openModalDeleteVideo } from "../../../../modals/delete/redux/slice";
import colors from "Assets/sass/core/vars.scss";

import { openSharedMediaModal } from "Redux/actions";
import moment from "moment";

const chooseDate = (date, format, dateformat) => {
  if (!date || !String(date).trim()) {
    return "....";
  }

  // data?.unitTime arrives pre-formatted as `h:mm:ss A, ${GetFormatForMoment(dateformat)}`
  // (see getFormattedDateByTimeZone in media/redux/slice.js); it must be re-parsed with that
  // same source format, since a locale like DD/MM/YYYY isn't reliably parseable by moment()/Date()
  // without an explicit format (that's what produced "Invalid date").
  const sourceFormat = `h:mm:ss A, ${GetFormatForMoment(dateformat)}`;
  return moment(date.trim(), sourceFormat, true).format(format);
};

const VideoPlayer = ({ data, vidFleetVideoRef }) => {
  const [activeCameraIndex, setActiveCameraIndex] = useState(0);
  const videoOptions = data?.videoOptions || {};
  const deviceData = data?.deviceData?.deviceData
    ? JSON.parse(data?.deviceData?.deviceData)
    : {};
  const parseMedia = JSON.parse(deviceData);
  const mediaType = parseMedia?.mediaType || "video";

  if (
    data?.type === "SmartWitness" ||
    videoOptions?.deviceModel === "SmartWitness"
  ) {
    return (
      <div style={{ width: "100%", height: "100%" }}>
        <SmartWitnessSnapshot
          ref={vidFleetVideoRef}
          vehicleTrail={data.deviceData}
          response={data}
          height={400}
        />
      </div>
    );
  }
  if (
    data?.type === "LightMetrics" ||
    videoOptions?.deviceModel === "lightMetric"
    || data?.type==="Mitac"
  ) {
    if (mediaType === "picture") {
      return (
        <div style={{ width: "100%", height: "150px" }}>
          <img
            style={{ width: "100%", height: "100%" }}
            src={videoOptions?.url}
            controls
          />
        </div>
      );
    }

    const videoUrls = videoOptions?.urls?.length ? videoOptions.urls : (videoOptions?.url ? [videoOptions.url] : []);

    if (videoUrls.length > 1) {
      const tabsHeight = 28;
      return (
        <Box style={{ width: "100%", overflow: "hidden" }}>
          <Tabs
            value={activeCameraIndex}
            onChange={(event, newValue) => setActiveCameraIndex(newValue)}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{ minHeight: tabsHeight, backgroundColor: "rgba(0,0,0,0.85)" }}
          >
            {videoUrls.map((videoUrl, index) => (
              <Tab
                key={index}
                icon={<VideocamIcon style={{ fontSize: 14 }} />}
                iconPosition="start"
                label={index + 1}
                sx={{
                  minHeight: tabsHeight,
                  minWidth: 44,
                  padding: "2px 8px",
                  color: "grey.500",
                  "&.Mui-selected": { color: "white" },
                }}
              />
            ))}
          </Tabs>
          <Carousel
            fullHeightHover={false}
            autoPlay={false}
            indicators={false}
            index={activeCameraIndex}
            onChange={(newIndex) => setActiveCameraIndex(newIndex)}
            animation="slide"
            height={300 - tabsHeight}
          >
            {videoUrls.map((videoUrl, index) => (
              <video
                key={index}
                style={{ width: "100%", height: "100%" }}
                src={videoUrl}
                controls
              />
            ))}
          </Carousel>
        </Box>
      );
    }

    return (
      <div style={{ width: "100%", height: "300px" }}>
        <video
          style={{ width: "100%", height: "100%" }}
          ref={null}
          src={videoUrls[0]}
          controls
        />
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: "500px" }}>
      <VidFleetVideo ref={vidFleetVideoRef} {...data?.videoOptions} />
    </div>
  );
};

export const MediaView = () => {
  const { id } = useParams();
  let { search } = useLocation();
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const refNotes = useRef();

  const query = new URLSearchParams(search);
  const deviceId = query.get("deviceId");
  const currentSubUser = null;
  const vidFleetVideoRef = useRef();

  const user = useSelector((state) => state.securityRedux.user);
  const loading = useSelector((state) => state.videoDetailMediaRedux.loading);
  const data = useSelector((state) => state.videoDetailMediaRedux.data);
  const notes = useSelector((state) => state.videoDetailMediaRedux.notes);
  const loadingNotes = useSelector(
    (state) => state.videoDetailMediaRedux.loadingNotes
  );
  const error = useSelector((state) => state.videoDetailMediaRedux.error);
  const refreshVideo = useSelector(
    (state) => state.videoDetailMediaRedux.refreshVideo
  );
  const { dateformat, timeZone } = user;
  const format = `${GetFormatForMoment(dateformat)} hh:mm:ss a`;

  const eventId = `${deviceId}-${id}`;

  useEffect(() => {
    return () => {
      dispatch(reset());
    };
  }, []);

  useEffect(() => {
    if (refreshVideo) {
      dispatch(reset({ refreshVideo: null }));

      if (
        refreshVideo.key == "STATUS" &&
        refreshVideo.ids &&
        refreshVideo.ids.find((x) => x.deviceId == deviceId && x.rowId == id)
      ) {
        if (refreshVideo.reviewStatus != data?.reviewStatus) {
          dispatch(getMediaDetail({ id, deviceId, messages }));

          NotificationManager.warning(
            messages["videoDetail_nexPreviousButton_updatedEvent"],
            messages["information"],
            3000,
            null,
            null,
            "warning"
          );
        }
      }

      /*             if (refreshVideo.key == "NOTES" && refreshVideo.rowId == id && refreshVideo.deviceId == deviceId) {
                dispatch(getMediaDetailNotes({ readingId: refreshVideo.readingId }))

                NotificationManager.warning(
                    messages["videoDetail_nexPreviousButton_updatedEvent"],
                    messages["information"],
                    3000,
                    null,
                    null,
                    "warning"
                );
            } */
    }
  }, [refreshVideo]);

  useEffect(() => {
    if (id && deviceId) dispatch(getMediaDetail({ id, deviceId, messages }));
  }, [id, deviceId]);

  useEffect(() => {
    if (error) {
      dispatch(reset({ error: null }));

      if (error.nextPreviousVideo) {
        NotificationManager.warning(
          messages["videoDetail_nexPreviousButton_DntExist"],
          messages["information"],
          3000,
          null,
          null,
          "warning"
        );
      } else {
        NotificationManager.warning(
          messages["videoDetail_error"],
          messages["error"],
          3000,
          null,
          null,
          "warning"
        );
      }
    }
  }, [error]);

  LogManagerConsole("VIDEO", "videoDetail", "data", data, loading);
  const reviewStates = [
    {
      id: "Unreviewed",
      label: messages["videoMainView_unreviewed"],
      color: colors["warning"],
    },
    {
      id: "Reviewed",
      label: messages["videoMainView_reviewed"],
      color: colors["brown"],
    },
    {
      id: "FalsePositive",
      label: messages["videoMainView_falsePositive"],
      color: colors["purple"],
    },
    {
      id: "Accepted",
      label: messages["videoMainView_accepted"],
      color: colors["green"],
    },
    {
      id: "Disputed",
      label: messages["videoMainView_disputed"],
      color: colors["red"],
    },
  ];

  const eventName =
    configEventCategory(messages)[data?.eventCode]?.eventName ||
    data?.eventName ||
    messages["notAvailable"];

  return (
    <>
      <Container maxWidth={false} className="container-view">
        {loading && <LinearProgress />}
        <Paper elevation={0}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "15px 20px",
              gap: "8px",
              width: "97%",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Breadcrumb
                links={[
                  {
                    title: messages["videoDetail_availableMedia"],
                    link: "/app/video/main",
                  },
                  {
                    title: `${chooseDate(data?.unitTime, format, dateformat)} / ${eventId}`,
                  },
                ]}
              />
            </div>
            {data?.type != "SmartWitness" && !data?.isDeleted && (
              <div style={{ flexShrink: 0 }}>
                <NextPreviousButtons
                  rowId={id}
                  deviceId={deviceId}
                  loading={loading}
                />
              </div>
            )}
          </div>

          {loading ? (
            <Skeleton
              variant="rectangular"
              animation="wave"
              height="60px"
              style={{ marginLeft: 10, marginRight: 10 }}
            />
          ) : (
            <div style={{ marginLeft: 10, marginRight: 10 }}>
              <Grid
                display="flex"
                flexWrap="wrap"
                gap="8px"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid item>
                  <span className="span-text-title">
                    {messages["videoDetail_event"]}
                  </span>
                  <span
                    className="span-text-content"
                    style={{ marginRight: 10 }}
                  >
                    {eventName}
                  </span>
                </Grid>

                <Grid item>
                  <span className="span-text-title">
                    {messages["videoDetail_datetime"]}
                  </span>
                  <span
                    className="span-text-content"
                    style={{ marginRight: 10 }}
                  >
                    {chooseDate(data?.unitTime, format, dateformat)}
                  </span>
                </Grid>

                <Grid item>
                  <span className="span-text-title">
                    {messages["videoDetail_address"]}
                  </span>
                  <span
                    className="span-text-content"
                    style={{ marginRight: 10 }}
                  >
                    {data?.address || messages["notAvailable"]}
                  </span>
                </Grid>

                {data?.tags?.length > 0 && (
                  <Grid item>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "end",
                        marginRight: 15,
                      }}
                    >
                      <div className="span-text-title">
                        {messages["videoDetail_tags"]}
                      </div>
                      <div>
                        <TagsGroup
                          options={data?.tags || []}
                          nameField="label"
                          colorField="color"
                        />
                      </div>
                    </div>
                  </Grid>
                )}

                <Grid item>
                  <span className="span-text-title">
                    {messages["videoDetail_vehicle"]}
                  </span>
                  <span
                    className="span-text-content"
                    style={{ marginRight: 10 }}
                  >
                    {data?.label || messages["notAvailable"]}
                  </span>
                </Grid>

                <Grid item>
                  <span className="span-text-title">
                    {messages["videoDetail_driver"]}
                  </span>
                  <span
                    className="span-text-content"
                    style={{ marginRight: 10 }}
                  >
                    {data?.driverName || messages["unassigned"]}
                  </span>
                </Grid>

                <Grid item>
                  <span className="span-text-title">
                    {messages["videoDetail_speed"]}
                  </span>

                  {data?.useEcuSpeed && (
                    <span
                      className="ecuChip"
                      style={{ background: styles.ecu }}
                    >
                      ECU
                    </span>
                  )}

                  <span
                    className="span-text-content"
                    style={{ marginRight: 10 }}
                  >
                    {data?.speed !== null && data?.speed !== undefined
                      ? `${speedFormatMPH(
                          data?.speed,
                          data?.unitType,
                          user.isKph
                        )}`
                      : messages["notAvailable"]}
                  </span>
                </Grid>

                {HasPermission(user, ["vidrw"], [261]) &&
                  data?.reviewStatus && (
                    <div style={{ display: "flex" }}>
                      <Pill
                        textField="label"
                        item={reviewStates?.find(
                          (reviewState) => reviewState?.id == data?.reviewStatus
                        )}
                        variant="filled"
                      />
                      {!data?.isDeleted && (
                        <IconButton
                          onClick={() => {
                            refNotes.current.createNewReview();
                          }}
                          size="xsmall"
                          icon="edit"
                        />
                      )}
                    </div>
                  )}

                <Grid item>
                  <span className="span-text-title">
                    {messages["videoMainView_event_id"]}:
                  </span>
                  <span
                    className="span-text-content"
                    style={{ marginRight: 10 }}
                  >
                    {eventId}
                  </span>
                </Grid>

                {useSupervisorPermission(user, currentSubUser) && (
                  <Grid item>
                    <span className="span-text-title">
                      {messages["videoMainView_supervisor"]}:
                    </span>
                    <span
                      className="span-text-content"
                      style={{ marginRight: 10 }}
                    >
                      {data?.supervisor}
                    </span>
                  </Grid>
                )}

                {data?.mediaDeletedByUsername && data?.isDeleted && (
                  <Grid item>
                    <span className="span-text-title">
                      {messages["videoMainView_deletedBy"]}:
                    </span>
                    <span
                      className="span-text-content"
                      style={{ marginRight: 10 }}
                    >
                      {data?.mediaDeletedByUsername}
                    </span>
                  </Grid>
                )}
              </Grid>
            </div>
          )}

          <div style={{ padding: 15 }}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              {data?.videoOptions ? (
                <Grid
                  item
                  xs={
                    data?.type === "SmartWitness" ||
                    data?.videoOptions?.deviceModel === "SmartWitness"
                      ? 8
                      : 6
                  }
                  style={{
                    marginRight:
                      (data?.type === "SmartWitness" ||
                        data?.videoOptions?.deviceModel === "SmartWitness") &&
                      30,
                  }}
                >
                  <div>
                    <div style={{ paddingBottom: 15, display: "flex" }}>
                      {!data?.isDeleted &&
                        data?.videoOptions?.deviceModel != "360" && (
                          <IconButton
                            icon="get_app"
                            tooltip={
                              data?.videoOptions?.deviceModel == "360"
                                ? messages["videoMainView_download360Error"]
                                : messages["videoDetail_download"]
                            }
                            disabled={
                              data?.videoOptions?.deviceModel == "360"
                                ? true
                                : false
                            }
                            onClick={(e) => {
                              if (e) e.preventDefault();
                              if (vidFleetVideoRef?.current) {
                                if (data?.videoOptions?.mode === "video") {
                                  vidFleetVideoRef?.current.downloadVideo();
                                } else if (
                                  data?.videoOptions?.mode === "picture"
                                ) {
                                  vidFleetVideoRef?.current.downloadPicture();
                                }
                              }
                            }}
                          />
                        )}

                      {!data?.isDeleted &&
                        HasPermission(user, ["dtlvdo"], [282]) && (
                          <IconButton
                            icon="delete"
                            tooltip={messages["videoDetail_delete"]}
                            onClick={() => {
                              dispatch(
                                openModalDeleteVideo({
                                  type: "media",
                                  items: [
                                    {
                                      label: `${
                                        configEventCategory(messages)[
                                          data.eventCode
                                        ]?.eventName || data.eventName
                                      } ${chooseDate(data?.unitTime, format, dateformat)}, ${
                                        data.label ? data.label + " / " : ""
                                      }${
                                        data.driverName ||
                                        messages["unassigned"]
                                      }`,
                                      id: { rowId: id, deviceId: deviceId },
                                    },
                                  ],
                                })
                              );
                            }}
                          />
                        )}

                      {!data?.isDeleted &&
                        data?.type !== "SmartWitness" &&
                        data?.videoOptions?.deviceModel !== "SmartWitness" && (
                          <IconButton
                            icon="share"
                            tooltip={messages["videoDetail_share"]}
                            onClick={(e) => {
                              dispatch(
                                openSharedMediaModal({
                                  open: true,
                                  items: [
                                    {
                                      entity: {
                                        id: id,
                                        name: "DeviceReading",
                                        label: `${data?.unitTime} / ${eventId}`,
                                      },
                                      deviceId: deviceId,
                                    },
                                  ],
                                })
                              );
                            }}
                          />
                        )}
                    </div>

                    {loading ? (
                      <Skeleton
                        variant="square"
                        animation="wave"
                        height={400}
                      />
                    ) : (
                      <VideoPlayer
                        data={data}
                        vidFleetVideoRef={vidFleetVideoRef}
                      />
                    )}
                  </div>
                </Grid>
              ) : (
                <div style={{ height: "35pc", width: "50%" }}>
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    height="6%"
                    width="20%"
                    style={{ marginBottom: "10px" }}
                  />
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    height="90%"
                  />
                </div>
              )}
              <Grid
                item
                xs={
                  data?.type === "SmartWitness" ||
                  data?.videoOptions?.deviceModel === "SmartWitness"
                    ? 3
                    : 6
                }
                style={{ padding: "10px 0 0 5px" }}
              >
                <NotesView
                  ref={refNotes}
                  data={notes}
                  loading={loadingNotes}
                  deviceId={deviceId}
                  id={id}
                  reviewStates={reviewStates}
                  status={data?.reviewStatus}
                />
              </Grid>
              {loading ? (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  height="200px"
                />
              ) : (
                <>
                  {data?.type !== "SmartWitness" &&
                    data?.videoOptions?.deviceModel !== "SmartWitness" && (
                      <Grid item xs={12} style={{ marginTop: 12 }}>
                        <Map
                          data={{
                            coords: {
                              lat: data?.latitude,
                              lng: data?.longitude,
                            },
                            eventName: data?.eventName,
                            eventCode: data?.eventCode,
                            heading: data?.heading,
                          }}
                          zoom={15}
                        />
                      </Grid>
                    )}
                </>
              )}
            </Grid>
          </div>
        </Paper>
        <DeleteVideoModal
          onClose={(refresh, type) => {
            if (refresh || type == "media")
              dispatch(getMediaDetail({ id, deviceId, messages }));
          }}
        />
        <WizardAlert />
      </Container>
    </>
  );
};

const NotesView = forwardRef(({ deviceId, id, reviewStates, status }, ref) => {
  const { messages } = useIntl();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.securityRedux.user);
  const notes = useSelector((state) => state.videoDetailMediaRedux.notes);
  const loadingNotes = useSelector(
    (state) => state.videoDetailMediaRedux.loadingNotes
  );
  const result = useSelector((state) => state.videoDetailMediaRedux.result);
  const loadingSaveNotes = useSelector(
    (state) => state.videoDetailMediaRedux.loadingSaveNotes
  );
  const loading = useSelector(
    (state) => state.videoDetailMediaRedux.loadingSaveNotes.loading
  );

  const { dateformat, timeZone } = user;
  const [showEditNote, setShowEditNote] = useState();
  const [showNewNote, setShowNewNote] = useState();
  const chatUsers = useSelector((state) => state.catalogsRedux.chatUsers);

  useEffect(() => {
    if (result) {
      dispatch(reset({ result: null }));
      setShowEditNote();
      setShowNewNote();

      if (result.action == "videoReview")
        NotificationManager.success(
          messages["videoDetail_success"],
          messages["success"],
          3000,
          null,
          null,
          "success",
          null
        );
    }
  }, [result]);

  useImperativeHandle(ref, () => ({
    createNewReview: () => {
      setShowNewNote("hasReviewState");
    },
  }));

  LogManagerConsole("VIDEO", "NotesView", notes);
  return (
    <>
      {(loading || loadingNotes || loadingSaveNotes) && <LinearProgress />}
      {!showEditNote && !showNewNote && (
        <>
          {loading || loadingNotes || loadingSaveNotes ? (
            <Skeleton
              variant="rectangular"
              animation="wave"
              height="40px"
              //width='20%'
              //className="video-details-text-box"
            />
          ) : (
            <div
              className="video-details-text-box"
              onClick={() => {
                setShowNewNote(true);
              }}
            >
              <span className="video-details-text-box-placeholder">
                {messages["videoDetail_addNote"]}
              </span>
            </div>
          )}
        </>
      )}
      <div
        style={{
          padding: 10,
          marginTop: 15,
          backgroundColor: "rgb(250, 250, 250)",
          maxHeight: 400,
          overflowX: "auto",
        }}
      >
        {showNewNote && (
          <NoteEditView
            initialData={{ rowId: id, deviceId: deviceId, status }}
            onSave={(values) => {
              if (showNewNote == "hasReviewState") {
                dispatch(saveVideoReview(values));
              } else {
                dispatch(saveNote(values));
              }
            }}
            onCancel={() => setShowNewNote(false)}
            hasReviewState={showNewNote == "hasReviewState"}
            reviewStates={reviewStates}
          />
        )}
        {!loading && notes?.length > 0 && (
          <>
            {notes?.map((note, index) => {
              const systemLabel = messages["videoDetail_system"];
              const isSystemOwner = note?.jsonValue?.username === 'system';
              const createdByAuthId = note?.createdBy || (isSystemOwner ? systemLabel : note?.jsonValue?.username);
              const updatedByAuthId = note.updatedBy || note.jsonValue?.username;

              let createUser = chatUsers?.find(
                (x) => x.authId == createdByAuthId
              );
              let updatedUser = chatUsers?.find(
                (x) => x.authId == updatedByAuthId
              );
              if (createdByAuthId?.includes("PM_")) {
                let splitVals = createdByAuthId.split("PM_");
                createUser = { label: `System Admin (${splitVals[1]})` };
              } else if (!createUser && createdByAuthId) {
                createUser = { label: createdByAuthId };
              } else if (createUser) {
                createUser = {
                  label: `${createUser?.label} ${
                    createUser?.entityName == "DRIVER" ? "(Driver)" : ""
                  }`,
                };
              }

              if (updatedByAuthId?.includes("PM_")) {
                let splitVals = updatedByAuthId.split("PM_");
                updatedUser = { label: `System Admin (${splitVals[1]})` };
              } else if (!updatedUser && updatedByAuthId) {
                updatedUser = { label: updatedByAuthId };
              }

              return (
                <div key={index}>
                  {index > 0 && (
                    <Divider style={{ margin: "10px 0px 10px 0px" }} />
                  )}

                  {showEditNote && showEditNote == note.id ? (
                    <NoteEditView
                      initialData={{
                        rowId: id,
                        deviceId: deviceId,
                        id: note?.id,
                        notes: note?.jsonValue?.description,
                        visibleToDriver: note?.visibleToDriver,
                      }}
                      onSave={(values) => {
                        dispatch(saveNote(values));
                      }}
                      onCancel={() => setShowEditNote()}
                    />
                  ) : (
                    <div>
                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        style={{
                          paddingBottom: 8,
                        }}
                      >
                        <Grid item>
                          <span style={{ fontWeight: "bold" }}>
                            {createUser.label || note?.createdBy}
                          </span>
                        </Grid>
                        <Grid item>
                          <Timezone
                            date={note?.createdOn}
                            dateFormat={dateformat}
                            timeFormat="hh:mm a"
                            timezone={timeZone}
                            className="grey-color"
                          />
                        </Grid>
                      </Grid>

                      <div style={{ fontSize: 16, overflowWrap: "break-word" }}>
                        {note.jsonValue?.action === "delete_events"
                          ? `(${messages["videoDetail_deletion_note"]}): `
                          : ""}
                        {note.jsonValue?.description}
                      </div>

                      {note?.fieldName == "videoReview" && (
                        <div
                          style={{
                            display: "flex",
                            //justifyContent: "center",
                            alignItems: "center",
                            paddingTop: 10,
                          }}
                        >
                          <div>
                            <Pill
                              className="pill-no-margin"
                              textField={"label"}
                              item={reviewStates?.find(
                                (reviewState) =>
                                  reviewState?.id == note.jsonValue?.oldStatus
                              )}
                              variant="filled"
                            />
                          </div>
                          <div
                            style={{
                              paddingBottom: 10,
                              paddingLeft: 10,
                              paddingRight: 10,
                            }}
                          >
                            <Icons name="arrow_forward" size="medium" />
                          </div>
                          <div>
                            <Pill
                              className="pill-no-margin"
                              textField={"label"}
                              item={reviewStates?.find(
                                (reviewState) =>
                                  reviewState?.id == note.jsonValue?.status
                              )}
                              variant="filled"
                            />
                          </div>
                        </div>
                      )}

                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        style={{
                          paddingTop: 8,
                        }}
                      >
                        <Grid item>
                          {note?.fieldName === "notes" && (
                            <Fragment>
                              <span
                                className="btn-link"
                                style={{ color: "grey", fontSize: 13 }}
                                onClick={() => {
                                  setShowEditNote(note.id);
                                  setShowNewNote(false);
                                }}
                              >
                                {messages["videoDetail_editNote"]}
                              </span>

                              <span> · </span>

                              <span
                                className="btn-link"
                                style={{ color: "grey", fontSize: 13 }}
                                onClick={() => {
                                  dispatch(
                                    openModalDeleteVideo({
                                      rowId: id,
                                      deviceId: deviceId,
                                      type: "notes",
                                      items: [
                                        {
                                          id: note.id,
                                          label: note.jsonValue?.description,
                                        },
                                      ],
                                    })
                                  );
                                }}
                              >
                                {messages["videoDetail_deleteMedia"]}
                              </span>
                            </Fragment>
                          )}
                        </Grid>

                        <div style={{ display: "flex" }}>
                          {note?.createdOn != note?.updatedOn && (
                            <IconButton
                              icon="edit"
                              size="xsmall"
                              tooltip={`${messages["videoDetail_editedBy"]} ${updatedUser?.label}`}
                            />
                          )}
                          {note?.visibleToDriver == 1 && (
                            <IconButton
                              icon="lock"
                              size="xsmall"
                              tooltip={messages["videoDetail_visibleToDriver"]}
                            />
                          )}
                        </div>
                      </Grid>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </>
  );
});

export const NoteEditView = ({
  initialData,
  onSave,
  hasReviewState,
  reviewStates,
  onCancel,
}) => {
  const [formData, setFormData] = useState(initialData);
  const { messages } = useIntl();
  const user = useSelector((state) => state.securityRedux.user);

  return (
    <div style={{ paddingBottom: 10 }}>
      <div>
        {HasPermission(user, ["vidrw"], [261]) && hasReviewState && (
          <div style={{ marginTop: 8, marginBottom: 5 }}>
            <PillsGroup
              label={messages["videoDetail_reviewStatus"]}
              onClick={(item) => {
                setFormData({ ...formData, status: item.id });
              }}
              options={reviewStates}
              size="dynamic"
              style={{
                margin: 3,
              }}
              values={[reviewStates.find((x) => x.id == formData.status)]}
              multicolor={true}
            />
          </div>
        )}
        <TextArea
          label={`* ${messages["videoDetail_notes"]}`}
          name="notes"
          placeholder={messages["videoDetail_addNote"]}
          maxLength={300}
          value={formData?.notes}
          onChange={(value) => {
            setFormData({ ...formData, notes: value });
          }}
        />
        <Label size="sm" style={{ color: 'gray' }}>{messages['videoDetail_notes_maxLength']}</Label>
      </div>

      {HasPermission(user, ["vidrw"], [261]) && (
        <div>
          <Checkbox
            label={messages["videoDetail_visibleToDriver"]}
            checked={formData?.visibleToDriver ? true : false}
            onChange={(event) => {
              setFormData({ ...formData, visibleToDriver: event });
            }}
            size="md"
          />
        </div>
      )}
      <div style={{ width: "100%", marginTop: 15 }}>
        <Button
          variant="secondary"
          onClick={() => {
            setFormData(null);
            onCancel && onCancel();
          }}
          className="btn-cancel"
        >
          {messages["cancel"]}
        </Button>
        {"  "}
        <Button
          variant="primary"
          isDisabled={
            (hasReviewState &&
              (!formData?.notes || formData?.notes?.length < 6)) ||
            (!hasReviewState &&
              (!formData?.notes || formData?.notes?.length < 6))
          }
          onClick={(e) => {
            let valuesResult = {
              ...initialData,
              jsonValue: {
                type: initialData.type || "admin",
                description: formData.notes,
              },
              visibleToDriver: formData.visibleToDriver ? 1 : 0,
            };

            if (hasReviewState)
              valuesResult = {
                ...initialData,
                status: formData.status,
                notes: formData.notes,
                visibleToDriver: formData.visibleToDriver ? 1 : 0,
              };
            onSave && onSave(valuesResult);
          }}
          className="btn-blue"
        >
          {messages["save"]}
        </Button>
      </div>
      <Divider style={{ margin: "10px 0px 10px 0px" }} />
    </div>
  );
};

export default MediaView;
